import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { scanService, adminService } from '../services/api';
import notificationService from '../services/notificationService';

export const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Helper function to get a safe user ID string
  const getSafeUserId = useCallback(() => {
    // First try to get the ID as a direct value
    if (user?.id) {
      // Handle object IDs (MongoDB ObjectId with _id field)
      if (typeof user.id === 'object') {
        if (user.id._id) {
          return user.id._id;
        }
        if (user.id.id) {
          return user.id.id;
        }
        if (user.id.username) {
          return user.id.username;
        }
      }
      
      // If it's a string, use it directly
      if (typeof user.id === 'string') {
        return user.id;
      }
    }
    
    // Fallback to username if available
    if (user?.username) {
      return user.username;
    }
    
    // Last resort
    return 'guest';
  }, [user]);
  
  // Track last load time to prevent too frequent reloads
  const [lastLoadTime, setLastLoadTime] = useState(0);
  const MIN_LOAD_INTERVAL = 5000; // 5 seconds minimum between loads
  
  // Load notifications from backend
  const loadNotifications = useCallback(async (forceLoad = false) => {
    if (!isAuthenticated || !user) return;
    
    // Prevent loading too frequently unless forced
    const now = Date.now();
    if (!forceLoad && (now - lastLoadTime < MIN_LOAD_INTERVAL)) {
      console.log(`Skipping notification load, last loaded ${Math.floor((now - lastLoadTime)/1000)}s ago`);
      return;
    }
    
    // Avoid setting loading state if we just loaded
    if (now - lastLoadTime > 1000) {
      setIsLoading(true);
    }
    setError(null);
    
    try {
      const data = await notificationService.getNotifications();
      setLastLoadTime(Date.now());
      
      if (Array.isArray(data)) {
        // Process notifications to deduplicate and filter blocked scans
        const uniqueNotifications = [];
        const seenIds = new Set();
        const seenKeys = new Set();
        
        for (const notification of data) {
          // Skip blocked scan completion notifications
          if (notification.type === 'SCAN_COMPLETED' && 
              notification.scanId && 
              BLOCKED_SCAN_IDS.has(notification.scanId)) {
            console.log(`Filtering out blocked scan notification during load: ${notification.scanId}`);
            continue;
          }
          
          // Skip if we've seen this ID already
          if (notification._id && seenIds.has(notification._id)) continue;
          if (notification._id) seenIds.add(notification._id);
          
          // Also deduplicate by content signature (scanId + type + message)
          const contentKey = `${notification.scanId || ''}|${notification.type}|${notification.message}`;
          if (seenKeys.has(contentKey)) continue;
          seenKeys.add(contentKey);
          
          uniqueNotifications.push(notification);
        }
        
        // Only update state if we have different notifications
        if (uniqueNotifications.length !== notifications.length || 
            JSON.stringify(uniqueNotifications) !== JSON.stringify(notifications)) {
          setNotifications(uniqueNotifications);
          setUnreadCount(uniqueNotifications.filter(notification => !notification.read).length);
        }
      } else {
        console.error('Received invalid notification data format:', data);
        if (notifications.length > 0) {
          console.log('Keeping existing notifications instead of clearing');
        } else {
          setNotifications([]);
          setUnreadCount(0);
        }
      }
    } catch (err) {
      console.error('Failed to load notifications:', err);
      setError('Failed to load notifications. Using local data instead.');
      
      // Show fallback message but don't block the UI
      setTimeout(() => {
        setError(null);
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user, notifications]);
  
  // Add a new notification
  const addNotification = useCallback(async (notification) => {
    if (!isAuthenticated || !user) return;
    
    try {
      const newNotification = await notificationService.createNotification({
        ...notification,
        userId: notification.userId || getSafeUserId()
      });
      
      setNotifications(prev => [newNotification, ...prev]);
      if (!newNotification.read) {
        setUnreadCount(prev => prev + 1);
      }
      
      return newNotification._id;
    } catch (err) {
      console.error('Failed to add notification:', err);
      return null;
    }
  }, [isAuthenticated, user, getSafeUserId]);
  
  // Mark a notification as read
  const markAsRead = useCallback(async (id) => {
    try {
      await notificationService.markAsRead(id);
      
      setNotifications(prev => 
        prev.map(notification => 
          notification._id === id 
            ? { ...notification, read: true } 
            : notification
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  }, []);
  
  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead();
      
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read: true }))
      );
      
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
    }
  }, []);
  
  // Remove a notification
  const removeNotification = useCallback(async (id) => {
    try {
      await notificationService.deleteNotification(id);
      
      const notification = notifications.find(n => n._id === id);
      const wasUnread = notification && !notification.read;
      
      // If removing a scan completion notification, allow it to be recreated if needed
      if (notification && notification.type === 'SCAN_COMPLETED' && notification.scanId) {
        setProcessedScans(prev => {
          const newSet = new Set(prev);
          newSet.delete(notification.scanId);
          return newSet;
        });
      }
      
      setNotifications(prev => prev.filter(notification => notification._id !== id));
      
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to remove notification:', err);
    }
  }, [notifications]);
  
  // Clear all notifications
  const clearAllNotifications = useCallback(async () => {
    try {
      // First, immediately clear the UI state
      setNotifications([]);
      setUnreadCount(0);
      
      // Clear all notification-related localStorage
      const userId = getSafeUserId();
      const keysToRemove = [];
      
      // Find all localStorage keys related to notifications
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.includes('notifications') || 
          key.includes('processedScans') ||
          key.startsWith(`notifications_${userId}`) ||
          key.startsWith(`processedScans_${userId}`)
        )) {
          keysToRemove.push(key);
        }
      }
      
      // Remove all found keys
      keysToRemove.forEach(key => {
        console.log(`Removing localStorage key: ${key}`);
        localStorage.removeItem(key);
      });
      
      // Call the service to clear API/localStorage
      await notificationService.clearAllNotifications();
      
      // Reset all tracking states
      setProcessedScans(new Set(BLOCKED_SCAN_IDS));
      setLastUpdateTimes({});
      
      // Update the last load time to prevent immediate reload
      setLastLoadTime(Date.now());
      
      // Immediately save the blocked scan IDs to prevent reappearance
      localStorage.setItem(`processedScans_${userId}`, JSON.stringify(Array.from(BLOCKED_SCAN_IDS)));
      
      console.log('Cleared all notifications and reset all tracking');
    } catch (err) {
      console.error('Failed to clear all notifications:', err);
      // Even if API call fails, keep the UI cleared
      setNotifications([]);
      setUnreadCount(0);
      console.log('UI cleared despite API error');
    }
  }, [user, notifications, getSafeUserId]);
  
  // Toggle notification panel
  const toggleNotifications = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);
  
  // Close notification panel
  const closeNotifications = useCallback(() => {
    setIsOpen(false);
  }, []);
  
  // Load notifications when the user changes or on mount
  const [isInitialized, setIsInitialized] = useState(false);
  
  useEffect(() => {
    if (isAuthenticated && user && !isInitialized) {
      // Set as initialized to prevent repeated loading
      setIsInitialized(true);
      
      // Check API health first
      let apiAvailable = false;
      
      const initializeNotifications = async () => {
        try {
          apiAvailable = await notificationService.checkApiHealth();
          console.log('Notification API available:', apiAvailable);
          
          // If API is available, cleanup old notifications first
          if (apiAvailable) {
            await notificationService.cleanupOldNotifications();
          }
          
          // Clean up the specific problematic notifications
          const blockedScanIds = Array.from(BLOCKED_SCAN_IDS);
          await notificationService.removeSpecificScanNotifications(blockedScanIds);
          console.log('Cleaned up problematic scan notifications');
        } catch (err) {
          console.error('Error checking notification API health:', err);
          setError('Using local notifications instead of server');
          apiAvailable = false;
        }
        
        // Force load initial notifications with the forceLoad flag
        await loadNotifications(true);
        
        // Pre-populate processed scans with existing completed scans to prevent re-notification
        await populateProcessedScans();
        
        // Add default notifications if needed (with delay to ensure we have current data)
        // Only add these on first load, not on subsequent refreshes
        setTimeout(async () => {
          try {
            // Get current notifications to check what exists
            const currentNotifications = await notificationService.getNotifications();
            
            // Check if the user already has a welcome notification
            const hasWelcomeNotification = currentNotifications.some(
              notification => notification.type === 'WELCOME'
            );
            
            // Only add welcome notification if there are no notifications at all
            // AND this is truly the first initialization (not after clearing)
            if (!hasWelcomeNotification && currentNotifications.length === 0 && processedScans.size === 0) {
              await addNotification({
                type: 'WELCOME',
                title: `Welcome, ${user.username || 'Guest'}!`,
                message: 'Thank you for using DedSec Technologies. Start by creating your first scan.',
                icon: 'USER',
                priority: 'INFO'
              });
            }
            
            // Check if we need to add a scan limit notification for non-admin users
            if (user.role !== 'admin' && currentNotifications.length === 0 && processedScans.size === 0) {
              const hasScanLimitNotification = currentNotifications.some(
                notification => notification.type === 'SCAN_LIMIT'
              );
              
              if (!hasScanLimitNotification) {
                const remainingScans = (user.allowedScans || 0) - (user.scansRun || 0);
                const scanNotificationType = remainingScans <= 2 ? 'WARNING' : 'INFO';
                
                await addNotification({
                  type: 'SCAN_LIMIT',
                  title: 'Scan Limit Status',
                  message: `You have ${remainingScans} scan${remainingScans !== 1 ? 's' : ''} remaining.`,
                  icon: 'TARGET',
                  priority: scanNotificationType
                });
              }
            } else if (user.role === 'admin' && currentNotifications.length === 0 && processedScans.size === 0) {
              const hasAdminNotification = currentNotifications.some(
                notification => notification.type === 'ADMIN_STATUS'
              );
              
              if (!hasAdminNotification) {
                // Add admin notification
                await addNotification({
                  type: 'ADMIN_STATUS',
                  title: 'Admin Access',
                  message: 'You have full administrative privileges.',
                  icon: 'SHIELD',
                  priority: 'INFO'
                });
              }
            }
          } catch (error) {
            console.error('Error adding default notifications:', error);
          }
        }, 2000);
      };
      
      initializeNotifications();
    }
  }, [isAuthenticated, user, loadNotifications, addNotification, notifications, isInitialized]);
  
  // Keep track of processed scans globally to persist across re-renders
  const [processedScans, setProcessedScans] = useState(new Set());
  const [lastUpdateTimes, setLastUpdateTimes] = useState({});

  // Mirror the mutable state the scan poller reads into refs, so the 60s polling
  // interval can depend only on [isAuthenticated, user] instead of being torn
  // down/recreated every time a notification (or processedScans/lastUpdateTimes)
  // changes. The poller reads .current; state writes stay as functional updates.
  const notificationsRef = useRef(notifications);
  const processedScansRef = useRef(processedScans);
  const lastUpdateTimesRef = useRef(lastUpdateTimes);
  useEffect(() => { notificationsRef.current = notifications; }, [notifications]);
  useEffect(() => { processedScansRef.current = processedScans; }, [processedScans]);
  useEffect(() => { lastUpdateTimesRef.current = lastUpdateTimes; }, [lastUpdateTimes]);
  
  // Blocklist for problematic scan IDs that should never create notifications
  const BLOCKED_SCAN_IDS = new Set([
    'test430_344473',
    'test5_251692', 
    'test45',
    'test',
    'oyo_990845',
    'Scan_154432',
    'housing_819875'
  ]);
  
  // Load and save processed scans to localStorage for persistence
  useEffect(() => {
    const userId = getSafeUserId();
    const savedProcessedScans = localStorage.getItem(`processedScans_${userId}`);
    if (savedProcessedScans) {
      try {
        const parsedScans = JSON.parse(savedProcessedScans);
        // Add blocked scan IDs to the parsed scans
        const allProcessedScans = [...parsedScans, ...Array.from(BLOCKED_SCAN_IDS)];
        setProcessedScans(new Set(allProcessedScans));
        console.log(`Loaded ${allProcessedScans.length} processed scans from localStorage (including blocked scans)`);
      } catch (error) {
        console.error('Error loading processed scans:', error);
        // At least set the blocked scan IDs
        setProcessedScans(new Set(BLOCKED_SCAN_IDS));
      }
    } else {
      // Initialize with blocked scan IDs
      setProcessedScans(new Set(BLOCKED_SCAN_IDS));
    }
  }, [getSafeUserId]);
  
  // Save processed scans to localStorage whenever it changes
  useEffect(() => {
    const userId = getSafeUserId();
    if (processedScans.size > 0) {
      localStorage.setItem(`processedScans_${userId}`, JSON.stringify(Array.from(processedScans)));
    }
  }, [processedScans, getSafeUserId]);
  
  // Function to populate processed scans with all existing completed scans
  const populateProcessedScans = useCallback(async () => {
    try {
      console.log('Populating processed scans with existing completed scans...');
      
      // Get all scans based on user role
      let scans = [];
      if (user?.role === 'admin') {
        try {
          scans = await adminService.getAllScans();
        } catch (error) {
          console.warn('Admin scan fetch failed, falling back to user scans:', error);
          scans = await scanService.getUserScans();
        }
      } else {
        scans = await scanService.getUserScans();
      }
      
      // Find all completed scans and mark them as processed
      const completedScanIds = scans
        .filter(scan => scan && scan.scanId && scan.status === 'FINISHED')
        .map(scan => scan.scanId);
      
             if (completedScanIds.length > 0) {
         setProcessedScans(prev => {
           const newSet = new Set([...prev, ...completedScanIds, ...Array.from(BLOCKED_SCAN_IDS)]);
           console.log(`Added ${completedScanIds.length} completed scans to processed list (total: ${newSet.size})`);
           return newSet;
         });
       } else {
         // Even if no new completed scans, ensure blocked scan IDs are in the set
         setProcessedScans(prev => new Set([...prev, ...Array.from(BLOCKED_SCAN_IDS)]));
       }
    } catch (error) {
      console.error('Error populating processed scans:', error);
    }
  }, [user]);
  
  // Poll for running scans and add notifications for completed scans
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    
    let interval;
    let isPolling = false;
    let lastPollTime = 0;
    const MIN_POLL_INTERVAL = 30000; // 30 seconds minimum between polls
    
    const checkRunningScans = async () => {
      // Prevent multiple simultaneous polls and respect minimum interval
      const now = Date.now();
      if (isPolling || (now - lastPollTime < MIN_POLL_INTERVAL)) return;
      
      console.log(`[NotificationContext] Checking scans... Processed scans: ${processedScansRef.current.size}, Current notifications: ${notificationsRef.current.length}`);

      try {
        isPolling = true;
        lastPollTime = now;

        // Scan-progress notifications are per-user, so always scope the poller to
        // the current user's scans. (Admins previously fetched every user's running
        // scans here, churning delete+create notification pairs system-wide.)
        let scans = await scanService.getUserScans();
        
        // Filter out invalid scans and deduplicate by scanId
        scans = scans
          .filter(scan => scan && scan.scanId)
          .reduce((unique, scan) => {
            const exists = unique.find(s => s.scanId === scan.scanId);
            if (!exists) unique.push(scan);
            return unique;
          }, []);
        
        // Create a map of existing notifications by scan ID and type
        const existingNotifications = {};
        notificationsRef.current.forEach(notif => {
          if (notif.scanId) {
            if (!existingNotifications[notif.scanId]) {
              existingNotifications[notif.scanId] = {};
            }
            existingNotifications[notif.scanId][notif.type] = notif;
          }
        });
        
        // Process running and completed scans
        let notificationUpdated = false;
        
        for (const scan of scans) {
          const scanNotifications = existingNotifications[scan.scanId] || {};
          const runningNotif = scanNotifications['SCAN_RUNNING'];
          const completedNotif = scanNotifications['SCAN_COMPLETED'];
          
          // Check if we should process this scan based on throttling rules
          const lastUpdateTime = lastUpdateTimesRef.current[scan.scanId] || 0;
          const timeSinceLastUpdate = now - lastUpdateTime;
          const minUpdateInterval = 60000; // 1 minute between scan notification updates
          
          if (timeSinceLastUpdate < minUpdateInterval) {
            console.log(`Skipping notification update for scan ${scan.scanId}, updated ${Math.floor(timeSinceLastUpdate/1000)}s ago`);
            continue;
          }
          
          if (scan.status === 'RUNNING') {
            // Only update running notification if progress changed significantly (>10%)
            const currentProgress = scan.progress || 0;
            const previousProgress = runningNotif ? (runningNotif.progress || 0) : 0;
            const progressDifference = Math.abs(currentProgress - previousProgress);
            
            if (progressDifference > 10) {
              if (runningNotif) {
                // Update existing notification through API
                try {
                  await notificationService.deleteNotification(runningNotif._id);
                  await addNotification({
                    type: 'SCAN_RUNNING',
                    title: 'Scan in Progress',
                    message: `Scan "${scan.name}" is ${currentProgress}% complete.`,
                    icon: 'REFRESH',
                    priority: 'INFO',
                    scanId: scan.scanId,
                    progress: currentProgress
                  });
                  setLastUpdateTimes(prev => ({ ...prev, [scan.scanId]: now }));
                  notificationUpdated = true;
                } catch (error) {
                  console.error('Failed to update running notification:', error);
                }
              } else {
                // Create new running notification
                try {
                  await addNotification({
                    type: 'SCAN_RUNNING',
                    title: 'Scan in Progress',
                    message: `Scan "${scan.name}" is ${currentProgress}% complete.`,
                    icon: 'REFRESH',
                    priority: 'INFO',
                    scanId: scan.scanId,
                    progress: currentProgress
                  });
                  setLastUpdateTimes(prev => ({ ...prev, [scan.scanId]: now }));
                  notificationUpdated = true;
                } catch (error) {
                  console.error('Failed to create running notification:', error);
                }
              }
            }
          } else if (scan.status === 'FINISHED' && 
                    scan.finished && 
                    !processedScansRef.current.has(scan.scanId) &&
                    !BLOCKED_SCAN_IDS.has(scan.scanId) &&
                    !completedNotif) {
            // Only notify for completed scans that haven't been processed before,
            // aren't in the blocklist, and don't have existing completion notifications
            
            // Additional check: only notify if scan finished recently (within last 30 minutes)
            // This prevents old scans from triggering notifications on system restart
            const scanFinishedTime = new Date(scan.finished).getTime();
            const thirtyMinutesAgo = Date.now() - (30 * 60 * 1000);
            
            if (scanFinishedTime > thirtyMinutesAgo) {
              try {
                await addNotification({
                  type: 'SCAN_COMPLETED',
                  title: 'Scan Completed',
                  message: `Scan "${scan.name}" for target "${scan.target}" has completed.`,
                  icon: 'CHECK',
                  priority: 'SUCCESS',
                  scanId: scan.scanId
                });
                
                // Mark this scan as processed
                setProcessedScans(prev => new Set([...prev, scan.scanId]));
                setLastUpdateTimes(prev => ({ ...prev, [scan.scanId]: now }));
                notificationUpdated = true;
                
                console.log(`Added completion notification for scan ${scan.scanId}`);
              } catch (error) {
                console.error('Failed to create completion notification:', error);
              }
            } else {
              // Mark old completed scans as processed without creating notifications
              setProcessedScans(prev => new Set([...prev, scan.scanId]));
              console.log(`Skipped notification for old completed scan ${scan.scanId}`);
            }
          }
          
          // If scan already has a completion notification, mark it as processed
          if (scan.status === 'FINISHED' && completedNotif) {
            setProcessedScans(prev => new Set([...prev, scan.scanId]));
          }
        }
        
        // Clean up old running notifications for scans that no longer exist or aren't running
        // But only if we haven't already made notification updates in this cycle
        if (!notificationUpdated) {
          const runningNotifications = notificationsRef.current.filter(n => n.type === 'SCAN_RUNNING');
          for (const notification of runningNotifications) {
            const scan = scans.find(s => s.scanId === notification.scanId);
            if (!scan || scan.status !== 'RUNNING') {
              try {
                await removeNotification(notification._id);
                notificationUpdated = true;
              } catch (error) {
                console.error('Failed to remove stale notification:', error);
              }
            }
          }
        }
        
        // Reload notifications only if we made updates
        if (notificationUpdated) {
          await loadNotifications();
        }
      } catch (error) {
        console.error('Error checking scan status:', error);
      } finally {
        isPolling = false;
      }
    };
    
    // Initial check after a delay to let the system stabilize
    const initialCheckTimeout = setTimeout(checkRunningScans, 3000);
    
    // Set up polling interval (60 seconds instead of 45)
    interval = setInterval(checkRunningScans, 60000);
    
    return () => {
      if (initialCheckTimeout) {
        clearTimeout(initialCheckTimeout);
      }
      if (interval) {
        clearInterval(interval);
      }
    };
    // Poller reads mutable state via refs above; keep the interval stable so it
    // isn't torn down/recreated on every notification change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);
  
  // Function to immediately clean up problematic notifications from localStorage
  const immediateCleanup = useCallback(() => {
    try {
      const userId = getSafeUserId();
      console.log('Performing immediate cleanup of problematic notifications...');
      
      // Clean up localStorage notifications
      const storageKey = `notifications_${userId}`;
      const stored = localStorage.getItem(storageKey);
      
      if (stored) {
        try {
          const notifications = JSON.parse(stored);
          const cleanedNotifications = notifications.filter(notification => {
            if (notification.type === 'SCAN_COMPLETED' && 
                notification.scanId && 
                BLOCKED_SCAN_IDS.has(notification.scanId)) {
              console.log(`Removing blocked notification from localStorage: ${notification.scanId}`);
              return false;
            }
            return true;
          });
          
          if (cleanedNotifications.length !== notifications.length) {
            localStorage.setItem(storageKey, JSON.stringify(cleanedNotifications));
            console.log(`Cleaned localStorage: removed ${notifications.length - cleanedNotifications.length} blocked notifications`);
          }
        } catch (parseError) {
          console.error('Error parsing stored notifications:', parseError);
          // If corrupted, just clear it
          localStorage.removeItem(storageKey);
        }
      }
      
      // Ensure blocked scans are in processed scans
      setProcessedScans(prev => new Set([...prev, ...Array.from(BLOCKED_SCAN_IDS)]));
      
      // Also clean up any other notification-related keys
      const keysToCheck = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('notifications')) {
          keysToCheck.push(key);
        }
      }
      
      keysToCheck.forEach(key => {
        try {
          const value = localStorage.getItem(key);
          if (value) {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
              const cleaned = parsed.filter(item => {
                if (item && item.type === 'SCAN_COMPLETED' && 
                    item.scanId && BLOCKED_SCAN_IDS.has(item.scanId)) {
                  return false;
                }
                return true;
              });
              
              if (cleaned.length !== parsed.length) {
                localStorage.setItem(key, JSON.stringify(cleaned));
                console.log(`Cleaned localStorage key ${key}`);
              }
            }
          }
        } catch (error) {
          console.error(`Error cleaning localStorage key ${key}:`, error);
        }
      });
      
    } catch (error) {
      console.error('Error during immediate cleanup:', error);
    }
  }, [getSafeUserId]);
  
  // Run immediate cleanup on component mount
  useEffect(() => {
    immediateCleanup();
  }, [immediateCleanup]);
  
  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        isOpen,
        isLoading,
        error,
        addNotification,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearAllNotifications,
        toggleNotifications,
        closeNotifications,
        refreshNotifications: loadNotifications
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use notification context
export const useNotifications = () => {
  const context = React.useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}; 